import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import Loading from '../../../../../molecules/Loading'
import Select from '../../../../../molecules/Select'

const useStyles = makeStyles((theme) => ({
  editorContainer: {
    border: 'var(--border-neutral-thin)',
    '& .editor-input': {
      boxShadow: '0 4px 2px -4px inset',
      overflow: 'auto',
      maxHeight: '50vh'
    }
  },
  header: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
    maxHeight: '60vh',
    overflow: 'auto'
  },
  summary: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.info.contrastText
  }
}))

const useSubmitter = (dataSource, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: changeDatasourceSchema } = dataCatalog.useChangeDatasourceSchema()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      schema: formData.code
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await changeDatasourceSchema(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the datasource description')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, onComplete, changeDatasourceSchema])

  return {
    onSubmit,
    processing,
    error
  }
}

function ChangeDatasourceSchemaForm ({ dataSource, onCancel, onComplete }) {
  const classes = useStyles()
  const [schema, setSchema] = useState(dataSource.schema.code || '')
  const { onSubmit, processing, error } = useSubmitter(dataSource, onComplete)
  const { data, isLoading } = dataCatalog.useListCatalogSchemaItems()
  const options = useMemo(() => {
    return (data?.schemas || []).map(s => ({
      value: s.code,
      label: `${s.code} (${s.displayName})`
    }))
  }, [data])

  if (isLoading) {
    return (
      <Loading />
    )
  }

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No data source provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select options={options} onChange={setSchema} value={schema} />
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ code: schema })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

ChangeDatasourceSchemaForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    subType: PropTypes.string.isRequired,
    description: PropTypes.string,
    schema: PropTypes.shape({
      code: PropTypes.string
    })
  }).isRequired,
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ChangeDatasourceSchemaForm
